import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

/* Husky Custom Code - Start */
import SignInLink, { SignInLinkStyles } from "@husky-hrc/Components/SignIn/SignInLink";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getLink, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import groupBy from "lodash/groupBy";
import { connect, ResolveThunks } from "react-redux";
/* Husky Custom Code - End */

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
    // Husky Custom Code
    linkType = "linkType",
    textCapitalize = "textCapitalize",
}

/* Husky Custom Code - Start */
interface OwnLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        linkColor: string;
        linkSubtitle: string;
        linkDisplay: "authenticated" | "unauthenticated" | "both";
        singleLinkType: "manual" | "sign-in";
    };
}
/* Husky Custom Code - End */

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: OwnLinkModel[]; // Husky Custom Code
        [fields.customCSS]: string;
        // Husky Custom Code
        [fields.linkType]: "manual" | "products";
        [fields.textCapitalize]: boolean;
    };
    extendedStyles?: LinkListStyles;
}

/* Husky Custom Code - Start */
const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const showMenu = false;
    const titleLink = getLink(state, ownProps.fields.titleLink);
    let productsNeeded = false;
    const { value: products, isLoading: isProductsLoading } = getProductsDataView(state, { pageSize: 10 });
    let fieldLinks = ownProps.fields.links;
    if (ownProps.fields.linkType === "products") {
        productsNeeded = true;
        if (products) {
            fieldLinks = products.map(product => {
                return {
                    fields: {
                        url: product.canonicalUrl,
                        title: product.productTitle,
                        openInNewWindow: false,
                        overriddenTitle: product.productTitle,
                        destination: {
                            type: "Url",
                            value: product.canonicalUrl,
                        },
                        linkColor: "text.main",
                        linkSubtitle: product.productLine?.name || "",
                        linkDisplay: "both",
                        singleLinkType: "manual",
                    },
                };
            });
        }
    }
    const links = mapLinks<
        OwnLinkModel,
        {
            openInNewWindow: boolean;
            overriddenTitle: string;
            linkColor: string;
            linkSubtitle: string;
            linkDisplay: "authenticated" | "unauthenticated" | "both";
            singleLinkType: "manual" | "sign-in";
        }
    >(state, fieldLinks, widgetLink => ({
        openInNewWindow: widgetLink.fields.openInNewWindow,
        overriddenTitle: widgetLink.fields.overriddenTitle,
        linkColor: widgetLink.fields.linkColor,
        linkSubtitle: widgetLink.fields.linkSubtitle,
        linkDisplay: widgetLink.fields.linkDisplay,
        singleLinkType: widgetLink.fields.singleLinkType,
    }));

    const groupedLinks = groupBy(links, link => link.linkSubtitle);
    return {
        showMenu,
        titleLink,
        groupedLinks,
        productsNeeded,
        products,
        isAuthenticated: state.context.session.isAuthenticated,
        isProductsLoading,
    };
};

const mapDispatchToProps = {
    loadProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
/* Husky Custom Code - End */

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    // Husky Custom Code
    mobileTitle?: ClickablePresentationProps;
    subtitle?: TypographyPresentationProps;
    linkTextStyles?: LinkPresentationProps;
    mobileTitleStyles?: InjectableCss;
    signInLinkStyles?: SignInLinkStyles;
    mobileIconStyles?: IconProps;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            padding: 0; // Husky Custom Code
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
            // Husky Custom Code
            color: ${getColor("danger.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-bottom: 18px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    /* Husky Custom Code - Start */
    title: {
        variant: "h4",
        css: css`
            font-size: 16px;
            font-weight: 900;
            letter-spacing: 1px;
        `,
    },
    mobileTitle: {
        css: css`
            font-size: 16px;
            font-weight: 900;
            letter-spacing: 1px;
            color: ${getColor("danger.main")};
            margin-bottom: 0;
        `,
    },
    subtitle: {
        variant: "headerSecondary",
        color: "common.backgroundContrast",
        css: css`
            letter-spacing: 0.8px;
            margin: 5px 0;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 0 0 15px 0;
        `,
    },
    linkTextStyles: {
        typographyProps: {
            variant: "headerTertiary",
            transform: "none",
        },
    },
    mobileTitleStyles: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    mobileIconStyles: {
        height: 27,
        width: 15,
        color: "danger.main",
        css: css`
            > svg {
                stroke-width: 4px;
            }
        `,
    },
    /* Husky Custom Code - End */
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

const LinkList: React.FC<Props> = ({
    fields,
    titleLink,
    groupedLinks,
    extendedStyles,
    products,
    productsNeeded,
    loadProducts,
    isAuthenticated,
    isProductsLoading,
}) => {
    const [styles] = React.useState(() => mergeToNew(linkListStyles, extendedStyles));
    const [loadRequested, setLoadCount] = React.useState(false);

    React.useEffect(() => {
        if (!loadRequested && productsNeeded && !isProductsLoading && (!products || products.length === 0)) {
            loadProducts({ pageSize: 10 });
            setLoadCount(true);
        }
    });
    const [showLinks, setShowLinks] = React.useState(false);

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal"
                ? `
                display: inline-block;
                padding: 1px 18px 0 0;

                &:last-child {
                    padding: 1px 0 0 0;
                }
            `
                : "padding: 1px 0 0 0;"}
            ${styles.linkWrapper?.css}
        `,
    };

    const titleStyles = css`
        text-transform: ${fields.textCapitalize ? "uppercase" : "initial"};
        ${styles.title?.css};
    `;

    const mobileTitleStyles = css`
        text-transform: ${fields.textCapitalize ? "uppercase" : "initial"};
        ${styles.mobileTitle?.css};
    `;

    const handleMobileTitleClick = () => {
        setShowLinks(!showLinks);
    };

    const linkVisible = (link: any) => {
        if (isAuthenticated) {
            return (
                (link.url || link.singleLinkType === "sign-in") &&
                (link.linkDisplay === "authenticated" || link.linkDisplay === "both")
            );
        }
        return (
            (link.url || link.singleLinkType === "sign-in") &&
            (link.linkDisplay === "unauthenticated" || link.linkDisplay === "both")
        );
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">
                <StyledWrapper css={alignmentStyles}>
                    <StyledWrapper {...styles.titleWrapper}>
                        {titleLink ? (
                            <Link href={titleLink.url}>{fields.title || titleLink.title}</Link>
                        ) : (
                            <Typography css={titleStyles}>{fields.title}</Typography>
                        )}
                    </StyledWrapper>
                    {Object.keys(groupedLinks).map((linkGroup, linkGroupIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={linkGroupIndex}>
                            {linkGroup !== "undefined" && <Typography {...styles.subtitle}>{linkGroup}</Typography>}
                            {groupedLinks[linkGroup].map(
                                (link, linkIndex) =>
                                    linkVisible(link) && (
                                        <StyledWrapper
                                            key={`${link.linkSubtitle}-${linkIndex}`} // eslint-disable-line react/no-array-index-key
                                            {...directionStyles}
                                        >
                                            {link.singleLinkType === "sign-in" ? (
                                                <SignInLink
                                                    label={link.overriddenTitle}
                                                    extendedStyles={styles.signInLinkStyles}
                                                />
                                            ) : (
                                                <Link
                                                    {...styles.linkTextStyles}
                                                    color={link.linkColor}
                                                    href={link.url}
                                                    target={link.openInNewWindow ? "_blank" : "_self"}
                                                >
                                                    {link.overriddenTitle || link.title}
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                    ),
                            )}
                        </React.Fragment>
                    ))}
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper css={alignmentStyles}>
                    <StyledWrapper {...styles.titleWrapper}>
                        {titleLink ? (
                            <Link href={titleLink.url}>{fields.title || titleLink.title}</Link>
                        ) : (
                            <StyledWrapper {...styles.mobileTitleStyles} onClick={handleMobileTitleClick}>
                                <Clickable
                                    css={mobileTitleStyles}
                                    onClick={handleMobileTitleClick}
                                    aria-expanded={showLinks}
                                    aria-label={`${fields.title} - ${translate(
                                        showLinks ? "Collapse Menu" : "Expand Menu",
                                    )}`}
                                >
                                    {fields.title}
                                </Clickable>
                                {fields.title && <Icon {...styles.mobileIconStyles} src={showLinks ? Minus : Plus} />}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    {showLinks &&
                        Object.keys(groupedLinks).map((linkGroup, linkGroupIndex) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={linkGroupIndex}>
                                {linkGroup !== "undefined" && <Typography {...styles.subtitle}>{linkGroup}</Typography>}
                                {groupedLinks[linkGroup].map(
                                    (link, linkIndex) =>
                                        linkVisible(link) && (
                                            <StyledWrapper
                                                key={`${link.linkSubtitle}-${linkIndex}`} // eslint-disable-line react/no-array-index-key
                                                {...directionStyles}
                                            >
                                                {link.singleLinkType === "sign-in" ? (
                                                    <SignInLink
                                                        label={link.overriddenTitle}
                                                        extendedStyles={styles.signInLinkStyles}
                                                    />
                                                ) : (
                                                    <Link
                                                        {...styles.linkTextStyles}
                                                        color={link.linkColor}
                                                        href={link.url}
                                                        target={link.openInNewWindow ? "_blank" : ""}
                                                    >
                                                        {link.overriddenTitle || link.title}
                                                    </Link>
                                                )}
                                            </StyledWrapper>
                                        ),
                                )}
                            </React.Fragment>
                        ))}
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LinkList),
    definition: {
        group: "Basic",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.linkType,
                editorTemplate: "RadioButtonsField",
                defaultValue: "manual",
                fieldType: "General",
                options: [
                    {
                        displayName: "Manual",
                        value: "manual",
                    },
                    {
                        displayName: "Products",
                        value: "products",
                    },
                ],
                isRequired: true,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.textCapitalize,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "singleLinkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "manual",
                        options: [
                            {
                                displayName: "Manual",
                                value: "manual",
                            },
                            {
                                displayName: "Sign-In",
                                value: "sign-in",
                            },
                        ],
                        isRequired: true,
                        tab: basicTab,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: link => link.fields.singleLinkType === "manual",
                        tab: basicTab,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: "linkSubtitle",
                        displayName: "Subtitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        tab: basicTab,
                    },
                    {
                        name: "linkDisplay",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "both",
                        options: [
                            {
                                displayName: "Authenticated",
                                value: "authenticated",
                            },
                            {
                                displayName: "Unauthenticated",
                                value: "unauthenticated",
                            },
                            {
                                displayName: "Both",
                                value: "both",
                            },
                        ],
                        tab: basicTab,
                    },
                    {
                        name: "linkColor",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "black",
                        tab: basicTab,
                    },
                ],
                isVisible: widget => widget.fields.linkType === "manual",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
